import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartBar, faUsers, faCheckCircle, faTimesCircle, 
  faClock, faExclamationTriangle, faSync 
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import config from '../config/config';
import './VerificationDashboard.css';

const VerificationDashboard = () => {
  const { getAccessToken } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      
      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/verifications/analytics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }
      
      const result = await response.json();
      if (result.status === 'success') {
        setAnalytics(result.data);
      } else {
        throw new Error(result.message || 'Failed to load analytics');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      toast.error('Failed to load analytics: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = {
    PENDING: '#ffc107',
    APPROVED: '#28a745',
    REJECTED: '#dc3545'
  };

  if (loading) {
    return (
      <div className="verification-dashboard">
        <div className="dashboard-loading">
          <FontAwesomeIcon icon={faSync} spin size="3x" />
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="verification-dashboard">
        <div className="dashboard-error">
          <FontAwesomeIcon icon={faExclamationTriangle} size="3x" />
          <p>No analytics data available</p>
          <button onClick={fetchAnalytics} className="btn-retry">
            <FontAwesomeIcon icon={faSync} /> Retry
          </button>
        </div>
      </div>
    );
  }

  const statusData = Object.entries(analytics.requestsByStatus || {}).map(([name, value]) => ({
    name,
    value,
    color: COLORS[name] || '#6c757d'
  }));

  const teamData = Object.entries(analytics.requestsByTeam || {})
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10); // Top 10 teams

  const typeData = Object.entries(analytics.requestsByType || {}).map(([name, value]) => ({
    name,
    value
  }));

  return (
    <div className="verification-dashboard">
      <div className="dashboard-header">
        <h2>
          <FontAwesomeIcon icon={faChartBar} /> Verification Analytics
        </h2>
        <button onClick={fetchAnalytics} className="btn-refresh" title="Refresh Analytics">
          <FontAwesomeIcon icon={faSync} />
        </button>
      </div>
      
      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card pending">
          <div className="card-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="card-content">
            <h3>{analytics.totalPending}</h3>
            <p>Pending Requests</p>
            {analytics.pendingOverThreshold > 0 && (
              <small className="warning">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {' '}{analytics.pendingOverThreshold} over {analytics.thresholdHours}h
              </small>
            )}
            {analytics.receivedToday > 0 && (
              <small className="info">{analytics.receivedToday} received today</small>
            )}
          </div>
        </div>
        
        <div className="summary-card approved">
          <div className="card-icon">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="card-content">
            <h3>{analytics.totalApproved}</h3>
            <p>Total Approved</p>
            <small>{analytics.approvedToday} approved today</small>
          </div>
        </div>
        
        <div className="summary-card rejected">
          <div className="card-icon">
            <FontAwesomeIcon icon={faTimesCircle} />
          </div>
          <div className="card-content">
            <h3>{analytics.totalRejected}</h3>
            <p>Total Rejected</p>
            <small>{analytics.rejectedToday} rejected today</small>
          </div>
        </div>
        
        <div className="summary-card rate">
          <div className="card-icon">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="card-content">
            <h3>{analytics.approvalRatePercentage?.toFixed(1) || 0}%</h3>
            <p>Approval Rate</p>
            {analytics.avgProcessingTimeMinutes && (
              <small>Avg: {analytics.avgProcessingTimeMinutes.toFixed(1)} min</small>
            )}
          </div>
        </div>
      </div>
      
      {/* Charts */}
      <div className="charts-grid">
        {/* Status Distribution */}
        {statusData.length > 0 && (
          <div className="chart-container">
            <h3>Status Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
        
        {/* Request Type Distribution */}
        {typeData.length > 0 && (
          <div className="chart-container">
            <h3>Request Type Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={typeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#3f51b5" />
                  <Cell fill="#9c27b0" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
        
        {/* Requests by Team */}
        {teamData.length > 0 && (
          <div className="chart-container full-width">
            <h3>Top Teams by Request Count</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={teamData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3f51b5" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        
        {/* Last 30 Days Trend */}
        {analytics.last30Days && analytics.last30Days.length > 0 && (
          <div className="chart-container full-width">
            <h3>Requests Over Last 30 Days</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.last30Days}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="requestsReceived" stroke="#8884d8" name="Received" />
                <Line type="monotone" dataKey="approved" stroke="#28a745" name="Approved" />
                <Line type="monotone" dataKey="rejected" stroke="#dc3545" name="Rejected" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationDashboard;

